// ============================================================
// TPO Terminal — Hauptseite.
// Watchlist-Sidebar + großer Hauptchart + synchronisiertes TPO-Overlay
// + zentrales Indikator-Menü. Ein gebündelter Stream; TPO nur aktiv.
// ============================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChartView, { ChartHandle, OverlayState, DrawTool } from './ChartView';
import Watchlist from './Watchlist';
import IndicatorMenu, { Settings, DEFAULT_SETTINGS } from './IndicatorMenu';
import { useMarketSocket } from './useMarketSocket';
import { UniverseItem, TickerMsg, KlineMsg, Bar, TIMEFRAMES, fmtPrice } from './types';
import { buildProfile, buildAccumulator, finalizeProfile, dayStartUTC, autoRowSize, TpoProfile, TpoAccumulator, RunningBracket } from './tpoEngine';
import { atr } from './indicators';
import { ContextMenu, MenuItem, AlarmDialog, SnapshotDialog, useFlash } from './TerminalDialogs';
import { usePhone, useTablet } from '../../hooks/useMediaQuery';
import './Terminal.css';

const API = '/api';

// Clipboard mit Fallback — die Prod-Instanz läuft per HTTP, dort ist
// navigator.clipboard nicht verfügbar (nur Secure Context / localhost).
function copyText(text: string) {
  try {
    if (navigator.clipboard && window.isSecureContext) { navigator.clipboard.writeText(text); return; }
  } catch { /* fällt durch */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.focus(); ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  } catch { /* still */ }
}

const Terminal: React.FC = () => {
  const navigate = useNavigate();
  const [universe, setUniverse] = useState<UniverseItem[]>([]);
  const [tickers, setTickers] = useState<Record<string, { last: number; changePct: number }>>({});
  const [symbol, setSymbol] = useState('BTCUSDT');
  const [tfCode, setTfCode] = useState('30');
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [tool, setTool] = useState<DrawTool>('none');
  const [magnet, setMagnet] = useState(false);
  const [hasPosSel, setHasPosSel] = useState(false);
  // Responsive: Watchlist-Drawer (Phone) + Toolbar-Overflow-Menü (Phone/Tablet).
  const phone = usePhone();
  const tablet = useTablet();
  const [watchlistOpen, setWatchlistOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  // Block D/H Overlays
  const [menu, setMenu] = useState<{ x: number; y: number; items: MenuItem[] } | null>(null);
  const [alarm, setAlarm] = useState<{ symbol: string; price: number } | null>(null);
  const [snap, setSnap] = useState<string | null>(null);
  const [flash, doFlash] = useFlash();
  const [profiles, setProfiles] = useState<{ today: TpoProfile | null; prev: TpoProfile | null }>({ today: null, prev: null });
  const [atrVal, setAtrVal] = useState<number | null>(null);
  const [chartLast, setChartLast] = useState(0);

  const chartRef = useRef<ChartHandle>(null);
  const tickerBuf = useRef<Record<string, { last: number; changePct: number }>>({});
  // Letzter Live-Preis je Symbol — nie geleert, Quelle fürs 1-s-TPO-Tick (Block C).
  const priceRef = useRef<Record<string, number>>({});
  // Gecachte Verteilung der abgeschlossenen Brackets + laufendes Bracket (Block C).
  const accRef = useRef<TpoAccumulator | null>(null);
  const runRef = useRef<RunningBracket | null>(null);

  const patch = useCallback((p: Partial<Settings>) => setSettings(s => ({ ...s, ...p })), []);

  const onKline = useCallback((k: KlineMsg) => { chartRef.current?.applyKline(k); }, []);
  const onTicker = useCallback((t: TickerMsg) => {
    tickerBuf.current[t.symbol] = { last: t.last, changePct: t.changePct };
    if (t.last > 0) priceRef.current[t.symbol] = t.last;
  }, []);

  // Chart-Bars → ATR + Referenzpreis für ATR-Linien.
  const onBars = useCallback((bars: Bar[]) => {
    setAtrVal(atr(bars, settings.atrPeriod));
    const last = bars[bars.length - 1];
    if (last) setChartLast(last.c);
  }, [settings.atrPeriod]);

  const { status, subscribeWatchlist, setActive } = useMarketSocket({ onTicker, onKline });

  useEffect(() => {
    fetch(`${API}/market/universe`).then(r => r.ok ? r.json() : []).then((u: UniverseItem[]) => {
      setUniverse(u);
      if (u.length && !u.some(x => x.symbol === 'BTCUSDT')) setSymbol(u[0].symbol);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    subscribeWatchlist();
    const i = setInterval(() => {
      if (Object.keys(tickerBuf.current).length) {
        setTickers(prev => ({ ...prev, ...tickerBuf.current }));
        tickerBuf.current = {};
      }
    }, 500);
    return () => clearInterval(i);
  }, [subscribeWatchlist]);

  useEffect(() => { setActive(symbol, tfCode); }, [symbol, tfCode, setActive]);

  const item = useMemo(() => universe.find(u => u.symbol === symbol), [universe, symbol]);
  const tickSize = item?.tick_size || 0.01;
  const live = tickers[symbol];
  const autoRow = useMemo(() => autoRowSize(tickSize, live?.last || chartLast || 1), [tickSize, live, chartLast]);

  // ---- TPO-Basis aus dediziertem 30m-Datensatz (unabhängig vom Chart-TF) ----
  // Abgeschlossene Brackets → gecachter Accumulator; laufendes Bracket separat.
  // Wird bei Symbol-/Settings-Wechsel und alle 30 s als Resync gerufen, NICHT pro Sekunde.
  const refreshTpoBase = useCallback(async () => {
    try {
      const r = await fetch(`${API}/market/klines?symbol=${symbol}&interval=30&limit=200`);
      if (!r.ok) return;
      const bars: Bar[] = await r.json();
      if (!bars.length) return;
      const ref = bars[bars.length - 1].c;
      const rowSize = settings.rowSizeOverride ?? autoRowSize(tickSize, ref);
      const cfg = { bracketMin: settings.bracketMin, valueAreaPct: settings.valueAreaPct, rowSize };
      const bracketSec = settings.bracketMin * 60;
      const nowSec = Math.floor(Date.now() / 1000);
      const todayStart = dayStartUTC(nowSec);
      const prevStart = todayStart - 86400;
      const curIdx = Math.floor((nowSec - todayStart) / bracketSec);

      const todayBars = bars.filter(b => dayStartUTC(Math.floor(b.openTime / 1000)) === todayStart);
      const bracketOf = (b: Bar) => Math.floor((Math.floor(b.openTime / 1000) - todayStart) / bracketSec);
      const completed = todayBars.filter(b => bracketOf(b) < curIdx);
      const runningBars = todayBars.filter(b => bracketOf(b) === curIdx);

      accRef.current = buildAccumulator(completed, cfg, todayStart);
      if (runningBars.length) {
        runRef.current = {
          bracketIdx: curIdx,
          hi: Math.max(...runningBars.map(b => b.h)),
          lo: Math.min(...runningBars.map(b => b.l)),
        };
      } else {
        const px = priceRef.current[symbol];
        runRef.current = px ? { bracketIdx: curIdx, hi: px, lo: px } : null;
      }

      const prev = buildProfile(bars.filter(b => dayStartUTC(Math.floor(b.openTime / 1000)) === prevStart), cfg, prevStart);
      setProfiles({ prev, today: finalizeProfile(accRef.current, runRef.current) });
    } catch { /* still */ }
  }, [symbol, tickSize, settings.bracketMin, settings.valueAreaPct, settings.rowSizeOverride]);

  useEffect(() => {
    accRef.current = null; runRef.current = null;
    refreshTpoBase();
    let ticks = 0;
    // 1-s-Tick: faltet NUR das laufende Bracket aus dem Live-Preis ein.
    const i = setInterval(() => {
      ticks++;
      if (ticks % 30 === 0) { refreshTpoBase(); return; } // periodischer Resync (30 s)
      const acc = accRef.current;
      if (!acc) return;
      const nowSec = Math.floor(Date.now() / 1000);
      const bracketSec = acc.bracketMin * 60;
      const idx = Math.floor((nowSec - acc.dayStartSec) / bracketSec);
      const run = runRef.current;
      if (run && run.bracketIdx !== idx) { refreshTpoBase(); return; } // Bracket-Wechsel → resync
      const price = priceRef.current[symbol];
      if (price && price > 0) {
        runRef.current = run
          ? { bracketIdx: idx, hi: Math.max(run.hi, price), lo: Math.min(run.lo, price) }
          : { bracketIdx: idx, hi: price, lo: price };
      }
      setProfiles(p => ({ prev: p.prev, today: finalizeProfile(acc, runRef.current) }));
    }, 1000);
    return () => clearInterval(i);
  }, [refreshTpoBase, symbol]);

  // ATR-SL/BE-Hilfslinien relativ zum letzten Preis.
  const atrLines = useMemo(() => {
    if (!settings.atrLines || atrVal == null || !chartLast) return undefined;
    const f = settings.atrFactor;
    return [
      { label: `SL +${f}×ATR`, price: chartLast + atrVal * f, color: '#ef4444' },
      { label: `SL -${f}×ATR`, price: chartLast - atrVal * f, color: '#ef4444' },
      { label: 'BE +1×ATR', price: chartLast + atrVal, color: '#3b82f6' },
      { label: 'BE -1×ATR', price: chartLast - atrVal, color: '#3b82f6' },
    ];
  }, [settings.atrLines, settings.atrFactor, atrVal, chartLast]);

  const overlay: OverlayState = useMemo(() => ({
    today: profiles.today, prev: profiles.prev, bracketMin: settings.bracketMin,
    show: { tpo: settings.tpo, mode: settings.mode, ib: settings.ib, singles: settings.singles, excess: settings.excess, prevLevels: settings.prevLevels, devLevels: settings.devLevels },
    atrLines,
  }), [profiles, settings, atrLines]);

  // Rechtsklick-Kontextmenü (Block D) aus ChartView-Cursor aufbauen.
  const onChartContext = useCallback((info: { clientX: number; clientY: number; price: number | null; time: number | null }) => {
    const ts = tickSize;
    const items: MenuItem[] = [
      { label: '↺ Chartansicht zurücksetzen', onClick: () => chartRef.current?.fitDay() },
      {
        label: info.price != null ? `⧉ Preis kopieren (${fmtPrice(info.price, ts)})` : '⧉ Preis kopieren',
        onClick: () => { if (info.price != null) { copyText(fmtPrice(info.price, ts)); doFlash('Preis kopiert'); } },
      },
      { label: '🔔 Alarm am Cursor-Preis', onClick: () => { if (info.price != null) setAlarm({ symbol, price: +info.price.toFixed(8) }); } },
      { label: '✎ Zeichnungen entfernen', onClick: () => chartRef.current?.clearDrawings() },
      { label: '⊘ Indikatoren entfernen', danger: true, onClick: () => setSettings(s => ({ ...s, ib: false, singles: false, excess: false, devLevels: false, prevLevels: false, atrLines: false })) },
    ];
    setMenu({ x: info.clientX, y: info.clientY, items });
  }, [symbol, tickSize, doFlash]);

  // Block C: ausgewählte Position ins bestehende Trade-Formular übernehmen.
  // Prefill via localStorage + Navigation zu /trades; gespeichert wird erst nach
  // manueller Bestätigung im Formular — nie automatisch.
  const savePositionAsTrade = useCallback(() => {
    const pos = chartRef.current?.getSelectedPosition();
    if (!pos) { doFlash('Keine Position ausgewählt'); return; }
    const d = new Date(pos.timeSec * 1000);
    const patch = {
      date: d.toISOString().split('T')[0],
      trade_time: d.toISOString().substring(11, 16),
      asset: pos.symbol,
      direction: pos.side,
      risk_reward: pos.rr ? String(pos.rr) : '',
      sl_distance: String(+Math.abs(pos.entry - pos.stop).toFixed(8)),
      tp_distance: String(+Math.abs(pos.target - pos.entry).toFixed(8)),
      comment: `TPO-Terminal ${pos.side.toUpperCase()} — Entry ${pos.entry} / SL ${pos.stop} / TP ${pos.target} (R:R ${pos.rr})`,
    };
    try { localStorage.setItem('tpo_trade_prefill', JSON.stringify(patch)); } catch {}
    navigate('/trades');
  }, [navigate, doFlash]);

  // Snapshot komponieren (Block H) und Dialog öffnen.
  const takeSnapshot = useCallback(() => {
    const img = chartRef.current?.snapshot();
    if (img) setSnap(img); else doFlash('Snapshot fehlgeschlagen');
  }, [doFlash]);

  const quickBtn = (active: boolean): React.CSSProperties => ({
    background: active ? '#8b5cf6' : 'transparent', color: active ? '#fff' : '#8b93a7',
    border: '1px solid ' + (active ? '#8b5cf6' : '#1e2539'), borderRadius: 6, padding: '5px 10px',
    fontSize: 11, fontWeight: 700, cursor: 'pointer',
  });

  const mobile = phone || tablet;

  // Kompakter Symbol-Header (eine Zeile, nichts abschneiden).
  const symbolBlock = (
    <div className="tb-symbol">
      <span className="tb-sym-base">{item?.base ?? symbol}</span>
      <span className="tb-sym-pair">{symbol} · Perp</span>
      {live && (
        <span className="tb-sym-quote" style={{ color: live.changePct >= 0 ? '#22c55e' : '#ef4444' }}>
          <span className="tb-sym-price">{fmtPrice(live.last, tickSize)}</span>
          <span className="tb-sym-chg">{live.changePct >= 0 ? '+' : ''}{live.changePct.toFixed(2)}%</span>
        </span>
      )}
    </div>
  );

  // Toolbar-Buttongruppen — Desktop: inline; Mobile: im „Mehr“-Menü.
  const tpoToggles = (
    <div className="tb-group">
      <button onClick={() => patch({ tpo: !settings.tpo })} style={quickBtn(settings.tpo)}>TPO</button>
      <button onClick={() => patch({ mode: settings.mode === 'letter' ? 'profile' : 'letter' })} style={quickBtn(false)}>
        {settings.mode === 'letter' ? 'Aa Letter' : '▮ Profil'}
      </button>
    </div>
  );
  const drawTools = (
    <div className="tb-group">
      {([['none', '⤢ Cursor'], ['trend', '╱ Linie'], ['rect', '▭ Rechteck'], ['hline', '─ H-Linie'], ['measure', '↔ Measure']] as [DrawTool, string][]).map(([t, label]) => (
        <button key={t} onClick={() => setTool(t)} title={label} style={quickBtn(tool === t)}>{mobile ? label : label.split(' ')[0]}</button>
      ))}
      <button onClick={() => setMagnet(m => !m)} style={quickBtn(magnet)} title="Magnet (an OHLC einrasten)">🧲{mobile ? ' Magnet' : ''}</button>
      <button onClick={() => chartRef.current?.undoDrawing()} style={quickBtn(false)} title="Rückgängig">↶{mobile ? ' Undo' : ''}</button>
      <button onClick={() => chartRef.current?.clearDrawings()} style={quickBtn(false)} title="Alle löschen">🗑{mobile ? ' Löschen' : ''}</button>
    </div>
  );
  const posTools = (
    <div className="tb-group">
      <button onClick={() => setTool('long')} style={{ ...quickBtn(tool === 'long'), color: tool === 'long' ? '#fff' : '#22c55e', borderColor: tool === 'long' ? '#22c55e' : '#1e2539', background: tool === 'long' ? '#22c55e' : 'transparent' }} title="Long-Position aufziehen">▲ Long</button>
      <button onClick={() => setTool('short')} style={{ ...quickBtn(tool === 'short'), color: tool === 'short' ? '#fff' : '#ef4444', borderColor: tool === 'short' ? '#ef4444' : '#1e2539', background: tool === 'short' ? '#ef4444' : 'transparent' }} title="Short-Position aufziehen">▼ Short</button>
      <button onClick={savePositionAsTrade} disabled={!hasPosSel} style={{ ...quickBtn(false), opacity: hasPosSel ? 1 : 0.4, cursor: hasPosSel ? 'pointer' : 'not-allowed' }} title="Ausgewählte Position als Trade speichern (Bestätigung im Formular)">💾 Trade</button>
    </div>
  );
  const viewTools = (
    <div className="tb-group">
      <button onClick={() => setTool('zoom')} style={quickBtn(tool === 'zoom')} title="Zoom-Box aufziehen">🔍 Zoom</button>
      <button onClick={() => chartRef.current?.autoScale()} style={quickBtn(false)} title="Auto-Scale Preisachse (Doppelklick auf Achse)">⤢ Auto</button>
      <button onClick={() => chartRef.current?.fitDay()} style={quickBtn(false)} title="Chartansicht zurücksetzen (Alt+R)">↺ Reset</button>
      <button onClick={takeSnapshot} style={quickBtn(false)} title="Snapshot → Trade/Journal">📷{mobile ? ' Snapshot' : ''}</button>
    </div>
  );
  const tfBar = (
    <div className="tf-bar">
      {TIMEFRAMES.map(tf => (
        <button key={tf.code} onClick={() => setTfCode(tf.code)} className="tf-chip" style={{
          background: tfCode === tf.code ? '#8b5cf6' : 'transparent', color: tfCode === tf.code ? '#fff' : '#8b93a7',
          border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer',
        }}>{tf.label}</button>
      ))}
    </div>
  );

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', background: '#0a0e1a', overflow: 'hidden' }} className="terminal-root">
      <Watchlist
        universe={universe} tickers={tickers} selected={symbol} onSelect={setSymbol} status={status}
        asDrawer={phone} open={watchlistOpen} onClose={() => setWatchlistOpen(false)}
        width={tablet ? 200 : 270}
      />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {mobile ? (
          /* ---- Mobile-Toolbar: kompakte Zeile + scrollbare TF-Chips + „Mehr“-Menü ---- */
          <div className="terminal-toolbar mobile">
            <div className="tb-row1">
              {phone && (
                <button className="tb-icon-btn" aria-label="Watchlist" onClick={() => setWatchlistOpen(true)}>☰</button>
              )}
              {symbolBlock}
              <div className="tb-row1-right">
                <IndicatorMenu s={settings} onChange={patch} atrValue={atrVal} autoRow={autoRow} />
                <button className="tb-icon-btn" aria-label="Mehr Werkzeuge" onClick={() => setMoreOpen(o => !o)}>⋯</button>
              </div>
            </div>
            {tfBar}
            {moreOpen && (
              <>
                <div className="tb-more-backdrop" onClick={() => setMoreOpen(false)} />
                <div className="tb-more" onClick={() => setMoreOpen(false)}>
                  {tpoToggles}{drawTools}{posTools}{viewTools}
                </div>
              </>
            )}
          </div>
        ) : (
          /* ---- Desktop-Toolbar (unverändertes Layout) ---- */
          <div className="terminal-toolbar">
            {symbolBlock}
            {tpoToggles}{drawTools}{posTools}{viewTools}
            <div className="tb-right">
              {tfBar}
              <IndicatorMenu s={settings} onChange={patch} atrValue={atrVal} autoRow={autoRow} />
            </div>
          </div>
        )}

        <div style={{ flex: 1, minHeight: 0 }}>
          <ChartView ref={chartRef} symbol={symbol} tfCode={tfCode} tickSize={tickSize} overlay={overlay} tool={tool} magnet={magnet} posQty={settings.posQty} onPositionChange={setHasPosSel} onBars={onBars} onContext={onChartContext} />
        </div>
      </div>

      {menu && <ContextMenu x={menu.x} y={menu.y} items={menu.items} onClose={() => setMenu(null)} />}
      {alarm && <AlarmDialog symbol={alarm.symbol} price={alarm.price} tickSize={tickSize} onClose={() => setAlarm(null)} onDone={doFlash} />}
      {snap && <SnapshotDialog image={snap} symbol={symbol} onClose={() => setSnap(null)} onDone={doFlash} />}
      {flash && (
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 80, background: '#161d33', color: '#fff', border: '1px solid #8b5cf6', borderRadius: 8, padding: '8px 16px', fontSize: 13, boxShadow: '0 8px 24px rgba(0,0,0,.5)' }}>{flash}</div>
      )}
    </div>
  );
};

export default Terminal;
